import { Alert, AppBar, Box, Container, Snackbar } from '@mui/material'
import { Breakpoint } from '@mui/system'
import React, { useContext, useEffect } from 'react'

import { FULL_DRAWER_WIDTH, ICONS_ONLY_DRAWER_WIDTH } from '../../config'
import { TABLET_WIDTH_BREAKPOINT } from '../../config/breakPoints'
import {
  NavVisibilityContext,
  NotificationContext,
  WindowSizeContext
} from '../../providers'
import { AppHeader } from '../AppHeader'
import { AppNav } from '../AppNav'
import { CenteredLoading } from '../CenteredLoading/CenteredLoading'

export const PAGE_TITLE_TEST_ID = 'PageTitle'
interface Props {
  title: string | JSX.Element
  maxWidth?: Breakpoint
  testId?: string
  isLoading?: boolean
  error?: boolean
  children?: React.ReactNode
}
export const Page: React.FC<Props> = ({
  title,
  testId,
  isLoading,
  error,
  children
}) => {
  useEffect(() => {
    localStorage.setItem('appPreference', 'main')
  }, [])

  const { width } = useContext(WindowSizeContext)
  const { iconsOnly } = useContext(NavVisibilityContext)

  // Icons-only mode only applies to the drawer rendered at sizes at or above
  // the tablet width breakpoint. At sizes below the tablet width breakpoint,
  // the drawer changes to the temporary variant, which means that when the
  // drawer is visible, the drawer is overlaid on top of the rest of the page
  // in a fashion similar to that of a modal. Due to this modal nature of the
  // drawer at smaller window widths, it makes sense for the drawer to not have
  // an icons-only mode.
  const overTabletBreakpoint = width >= TABLET_WIDTH_BREAKPOINT

  const drawerWidth =
    iconsOnly && overTabletBreakpoint
      ? ICONS_ONLY_DRAWER_WIDTH
      : FULL_DRAWER_WIDTH
  document.title = `${title} - InTune Auto`
  const {
    notice,
    setNotice,
    error: errorNotice,
    setError
  } = useContext(NotificationContext)
  return (
    <div data-testid={testId}>
      <AppBar
        position="fixed"
        variant="outlined"
        elevation={0}
        sx={{
          bgcolor: 'primary.light',
          width: {
            sm:
              width >= TABLET_WIDTH_BREAKPOINT
                ? `calc(100% - ${drawerWidth}px)`
                : '100%'
          },
          ml: { sm: width >= TABLET_WIDTH_BREAKPOINT ? `${drawerWidth}px` : 0 },
          borderBottom: '1px solid rgba(0, 0, 0, .24)',
          borderTop: 'none',
          borderLeft: 'none',
          borderRight: 'none'
        }}>
        <AppHeader />
      </AppBar>
      <AppNav />
      <Box
        sx={{
          ml: {
            sm: width >= TABLET_WIDTH_BREAKPOINT ? `${drawerWidth}px` : 0
          },
          flexGrow: 1,
          height: '100vh',
          overflow: 'auto'
        }}>
        <Box visibility={'hidden'}>
          <AppHeader />
        </Box>
        <Container maxWidth={false} sx={{ padding: 2 }}>
          {isLoading ? (
            <CenteredLoading />
          ) : error ? (
            <>Sorry, nothing here.</>
          ) : (
            children
          )}
        </Container>
      </Box>
      <Snackbar
        open={!!notice}
        autoHideDuration={5000}
        onClose={() => {
          setNotice('')
        }}>
        <Alert severity="success" variant="filled">
          {notice}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!errorNotice}
        autoHideDuration={5000}
        onClose={() => {
          setError('')
        }}>
        <Alert severity="error" variant="filled">
          {errorNotice}
        </Alert>
      </Snackbar>
    </div>
  )
}
